import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {HttpErrorResponse} from '@angular/common/http';
import {Subject} from 'rxjs';
import {filter, finalize, switchMap, takeUntil} from 'rxjs/operators';
import {FolderService} from '../../../services/folder/folder.service';
import {NotificationService} from '../../../services/common/notification.service';
import {
  FOLDER_ACCESS_LABELS,
  FOLDER_ITEM_TYPES,
  Folder,
  FolderItem,
  FolderItemType,
  FolderItemTypeInfo,
  folderUserName
} from '../../../dtos/folder/folder';
import {FolderActionsService} from '../../../services/folder/folder-actions.service';
import {folderErrorMessage, MAX_FOLDER_ITEMS} from '../../../shared/folder/folder-utils';
import {ConfirmationDialogComponent} from '../../common/confirmation-dialog/confirmation-dialog.component';

/**
 * a single Sammelmappe with all its entries
 */
@Component({
  selector: 'app-folder-detail',
  templateUrl: './folder-detail.component.html',
  styleUrls: ['./folder-detail.component.scss'],
  standalone: false
})
export class FolderDetailComponent implements OnInit, OnDestroy {

  readonly maxItems = MAX_FOLDER_ITEMS;

  readonly accessLabels = FOLDER_ACCESS_LABELS;
  readonly folderUserName = folderUserName;

  folder: Folder;
  loading = true;
  /** set when the folder could not be loaded, e.g. no access (anymore) */
  loadError: string;
  selection = new Map<FolderItemType, Set<number>>(FOLDER_ITEM_TYPES.map(t => [t.type, new Set<number>()]));

  private destroy$ = new Subject<void>();

  constructor(private route: ActivatedRoute,
              private router: Router,
              private dialog: MatDialog,
              private folderService: FolderService,
              private folderActions: FolderActionsService,
              private notificationService: NotificationService) {
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.clearSelection();
      this.load(Number(params.get('id')));
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(id: number = this.folder?.id): void {
    this.loading = true;
    this.folderService.getFolder(id)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: folder => {
          this.folder = folder;
          this.loadError = null;
          this.dropStaleSelection();
        },
        error: (error: HttpErrorResponse) => {
          this.folder = null;
          this.loadError = error.status === 403 || error.status === 404
            ? 'Diese Sammelmappe existiert nicht (mehr) oder Sie haben keinen Zugriff darauf.'
            : folderErrorMessage(error);
        }
      });
  }

  /* -------------------- access -------------------- */

  get isOwner(): boolean {
    return this.folder?.access === 'OWNER';
  }

  get canEdit(): boolean {
    return this.isOwner || this.folder?.access === 'EDIT';
  }

  /** item types that are contained in the list, in display order */
  get sections(): FolderItemTypeInfo[] {
    return this.folder ? FOLDER_ITEM_TYPES.filter(t => this.folder[t.list]?.length > 0) : [];
  }

  /* -------------------- selection -------------------- */

  get selectedItems(): FolderItem[] {
    const items: FolderItem[] = [];
    this.selection.forEach((ids, type) => ids.forEach(id => items.push({type, id})));
    return items;
  }

  clearSelection(): void {
    this.selection.forEach(ids => ids.clear());
  }

  /* -------------------- actions -------------------- */

  accept(): void {
    this.folderActions.accept(this.folder).subscribe(() => this.load());
  }

  leave(): void {
    const pending = this.folder.pending;
    this.folderActions.leave(this.folder, pending).subscribe(() => this.router.navigate(['/sammelmappen']));
  }

  rename(): void {
    this.folderActions.rename(this.folder).subscribe(updated => this.folder = updated);
  }

  duplicate(): void {
    this.folderActions.duplicate(this.folder).subscribe(copy => this.router.navigate(['/sammelmappen', copy.id]));
  }

  share(): void {
    this.folderActions.share(this.folder).subscribe();
  }

  export(): void {
    this.folderActions.export(this.folder);
  }

  delete(): void {
    this.folderActions.delete(this.folder).subscribe(() => this.router.navigate(['/sammelmappen']));
  }

  copySelectedToFolder(): void {
    this.folderActions.addToFolder(this.selectedItems, this.folder.id).subscribe(() => this.clearSelection());
  }

  removeSelected(): void {
    const items = this.selectedItems;
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        headline: 'Einträge entfernen',
        content: (items.length === 1 ? 'Der ausgewählte Eintrag wird' : 'Die ' + items.length + ' ausgewählten Einträge werden')
          + ' aus der Sammelmappe „' + this.folder.name + '“ entfernt. Die Einträge selbst bleiben erhalten.',
        action: 'Entfernen'
      }
    }).afterClosed().pipe(
      filter(confirmed => !!confirmed),
      switchMap(() => this.folderService.removeItems(this.folder.id, items))
    ).subscribe({
      next: () => {
        this.notificationService.openSnackBar(items.length === 1
          ? '1 Eintrag entfernt.' : items.length + ' Einträge entfernt.');
        this.clearSelection();
        this.load();
      },
      error: error => this.notificationService.openSnackBar(folderErrorMessage(error, {
        403: 'Sie dürfen diese Sammelmappe nicht bearbeiten.'
      }))
    });
  }

  private dropStaleSelection(): void {
    FOLDER_ITEM_TYPES.forEach(t => {
      const ids = new Set(this.folder[t.list].map(e => e.id));
      const selected = this.selection.get(t.type);
      selected.forEach(id => {
        if (!ids.has(id)) {
          selected.delete(id);
        }
      });
    });
  }
}
